import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeft, History, Lock, MessageSquare, Search } from 'lucide-react';
import * as historialApi from '@/api/copiloto-historial.api';
import { usePermisoHistorial } from '@/hooks/usePermisoHistorial';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import {
  ORIGENES_HISTORIAL,
  FILTROS_VACIOS,
  consultaDeHistorial,
  etiquetaDeOrigen,
  etiquetaDeTipo,
  hayFiltros,
  juntarPorId,
  type FiltrosHistorial,
} from '@/lib/copiloto-historial';
import { cn } from '@/lib/cn';

/**
 * EL HISTORIAL DEL COPILOTO.
 *
 * Todo lo que cada persona del equipo habló con el asistente, venga de donde
 * venga: el botón flotante, la pantalla del asistente, WhatsApp, la puesta en
 * marcha o un pedido de cambio. Nada se borra.
 *
 * SOLO LECTURA, a propósito: no hay botón para borrar ni para editar. Un
 * historial que se puede tocar no sirve para reconstruir qué pasó.
 *
 * Quién la abre no lo decide el rol del panel: lo contesta la API (el dueño de
 * la cuenta o un dueño del directorio del asistente). Cada conversación que se
 * abre queda en el registro de acciones — lo dice la pantalla, para que nadie se
 * sorprenda.
 */

const fechaCorta = new Intl.DateTimeFormat('es-DO', { dateStyle: 'medium' });
const fechaYHora = new Intl.DateTimeFormat('es-DO', { dateStyle: 'medium', timeStyle: 'short' });

export function CopilotoHistorial() {
  const permiso = usePermisoHistorial();

  if (permiso.cargando) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!permiso.puedeVer) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <EmptyState
          icon={Lock}
          title="Esta pantalla es solo para el dueño"
          description="El historial de lo que el equipo habló con el asistente lo ven el dueño de la cuenta y quien figure como dueño en el directorio del asistente."
        />
      </div>
    );
  }

  return <HistorialConPermiso />;
}

function HistorialConPermiso() {
  const [filtros, setFiltros] = useState<FiltrosHistorial>(FILTROS_VACIOS);
  // El texto se aplica al buscar, no con cada tecla: cada búsqueda recorre
  // todos los mensajes de todos.
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);

  const cambiar = (parcial: Partial<FiltrosHistorial>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
    setAbierto(null);
  };

  const personas = useQuery({
    queryKey: ['copiloto', 'historial', 'personas'],
    queryFn: historialApi.listarPersonas,
  });

  const hilos = useInfiniteQuery({
    queryKey: ['copiloto', 'historial', 'hilos', filtros],
    queryFn: ({ pageParam }) => historialApi.listarHilos(consultaDeHistorial(filtros, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.siguiente,
  });

  const listaDeHilos = juntarPorId(hilos.data?.pages.map((p) => p.hilos) ?? []);

  return (
    <div className="flex h-full min-h-0">
      {/* ───────────────────────────── la lista y los filtros */}
      <aside
        className={cn(
          'w-full shrink-0 flex-col border-r border-surface-border bg-white md:flex md:w-96 dark:border-slate-800 dark:bg-slate-900',
          abierto ? 'hidden' : 'flex',
        )}
      >
        <div className="space-y-2 border-b border-surface-border p-3 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-brand-600" />
            <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Historial del copiloto
            </h1>
          </div>
          <p className="text-xs text-slate-500">
            Todo lo que el equipo habló con el asistente. Solo lectura: acá no se borra nada.
          </p>

          <form
            className="relative"
            onSubmit={(e) => {
              e.preventDefault();
              cambiar({ q: texto });
            }}
          >
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Buscar palabras y apretar Enter…"
              aria-label="Buscar en lo que se habló"
              className="w-full rounded-md border border-surface-border bg-transparent py-1.5 pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
            />
          </form>

          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500">
              Persona
              <select
                value={filtros.persona}
                onChange={(e) => cambiar({ persona: e.target.value })}
                className="mt-0.5 w-full rounded-md border border-surface-border bg-transparent px-2 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              >
                <option value="">Todas</option>
                {personas.data?.personas.map((p) => (
                  <option key={p.clave} value={p.clave}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-500">
              Desde dónde
              <select
                value={filtros.origen}
                onChange={(e) => cambiar({ origen: e.target.value })}
                className="mt-0.5 w-full rounded-md border border-surface-border bg-transparent px-2 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              >
                <option value="">Todos</option>
                {ORIGENES_HISTORIAL.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.etiqueta}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-500">
              Desde el día
              <input
                type="date"
                value={filtros.desde}
                onChange={(e) => cambiar({ desde: e.target.value })}
                className="mt-0.5 w-full rounded-md border border-surface-border bg-transparent px-2 py-1 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              />
            </label>
            <label className="text-xs text-slate-500">
              Hasta el día
              <input
                type="date"
                value={filtros.hasta}
                onChange={(e) => cambiar({ hasta: e.target.value })}
                className="mt-0.5 w-full rounded-md border border-surface-border bg-transparent px-2 py-1 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              />
            </label>
          </div>

          {hayFiltros(filtros) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTexto('');
                cambiar(FILTROS_VACIOS);
              }}
            >
              Quitar filtros
            </Button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {hilos.isLoading && <Spinner />}

          {hilos.isError && (
            <p className="px-2 py-3 text-sm text-red-600">
              No pude traer las conversaciones. Prueba de nuevo en un momento.
            </p>
          )}

          {!hilos.isLoading && !hilos.isError && listaDeHilos.length === 0 && (
            <p className="px-2 py-3 text-sm text-slate-500">
              {hayFiltros(filtros)
                ? 'No hay conversaciones con estos filtros.'
                : 'Todavía nadie habló con el asistente.'}
            </p>
          )}

          {listaDeHilos.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => setAbierto(h.id)}
              className={cn(
                'mb-1 flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition',
                abierto === h.id
                  ? 'bg-brand-50 dark:bg-slate-800'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-800',
              )}
            >
              <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {h.persona.nombre}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {fechaCorta.format(new Date(h.ultima))}
                  </span>
                </span>
                <span className="block truncate text-xs text-slate-600 dark:text-slate-300">
                  {h.titulo}
                </span>
                <span className="line-clamp-2 text-xs text-slate-500">{h.vistaPrevia}</span>
                <span className="mt-1 flex flex-wrap items-center gap-1">
                  {h.origenes.map((o) => (
                    <Badge key={o} variant="info">
                      {etiquetaDeOrigen(o)}
                    </Badge>
                  ))}
                  <span className="text-[11px] text-slate-400">
                    {h.cantidad} {h.cantidad === 1 ? 'mensaje' : 'mensajes'}
                  </span>
                </span>
              </span>
            </button>
          ))}

          {hilos.hasNextPage && (
            <div className="px-2 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                loading={hilos.isFetchingNextPage}
                onClick={() => void hilos.fetchNextPage()}
              >
                Cargar más conversaciones
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* ───────────────────────────── la conversación */}
      <section
        className={cn('min-h-0 min-w-0 flex-1 flex-col md:flex', abierto ? 'flex' : 'hidden')}
      >
        {abierto ? (
          <Conversacion id={abierto} onVolver={() => setAbierto(null)} />
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <EmptyState
              icon={History}
              title="Elige una conversación"
              description="A la izquierda están todas, las más recientes primero. Abrir una queda anotado en el registro de acciones."
            />
          </div>
        )}
      </section>
    </div>
  );
}

function Conversacion({ id, onVolver }: { id: string; onVolver: () => void }) {
  const hilo = useInfiniteQuery({
    queryKey: ['copiloto', 'historial', 'hilo', id],
    queryFn: ({ pageParam }) => historialApi.traerHilo(id, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.siguiente,
  });

  const cabecera = hilo.data?.pages[0]?.hilo;
  const mensajes = juntarPorId(hilo.data?.pages.map((p) => p.mensajes) ?? []);

  return (
    <>
      <header className="flex items-center gap-2 border-b border-surface-border px-4 py-3 dark:border-slate-800">
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden"
          aria-label="Volver a la lista"
          onClick={onVolver}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-slate-800 dark:text-slate-100">
            {cabecera?.titulo ?? 'Conversación'}
          </h2>
          {cabecera && (
            <p className="truncate text-xs text-slate-500">
              {cabecera.persona.nombre}
              {cabecera.persona.telefono ? ` · ${cabecera.persona.telefono}` : ''}
              {' · Solo lectura'}
            </p>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {hilo.isLoading && <Spinner />}

        {hilo.isError && (
          <p className="text-sm text-red-600">
            No pude abrir esta conversación. Puede que ya no exista.
          </p>
        )}

        {!hilo.isLoading && !hilo.isError && mensajes.length === 0 && (
          <p className="text-sm text-slate-500">Esta conversación no tiene mensajes.</p>
        )}

        {mensajes.map((m) => {
          const esPersona = m.rol === 'user';
          const marca = etiquetaDeTipo(m.tipo);
          return (
            <div key={m.id} className={cn('flex', esPersona ? 'justify-end' : 'justify-start')}>
              <div className="max-w-[80%]">
                <div
                  className={cn(
                    'whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                    esPersona
                      ? 'bg-brand-600 text-white'
                      : marca
                        ? 'border border-amber-300 bg-amber-50 text-slate-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-slate-100'
                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                  )}
                >
                  {m.contenido}
                </div>
                <div
                  className={cn(
                    'mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-400',
                    esPersona ? 'justify-end' : 'justify-start',
                  )}
                >
                  <span>
                    {esPersona
                      ? (m.autor.nombre ?? cabecera?.persona.nombre ?? 'Persona')
                      : 'Asistente'}
                  </span>
                  <span>· {fechaYHora.format(new Date(m.createdAt))}</span>
                  <Badge variant="info">{etiquetaDeOrigen(m.origen)}</Badge>
                  {marca && <Badge variant="warning">{marca}</Badge>}
                </div>
              </div>
            </div>
          );
        })}

        {hilo.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              loading={hilo.isFetchingNextPage}
              onClick={() => void hilo.fetchNextPage()}
            >
              Cargar más mensajes
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
